import React, { FunctionComponent, useContext } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Button, Close } from "@cmsgov/ds-medicare-gov";
import { AppContext } from "../../../app/store";
import { Plan } from "../../../@types";
import {
  useTranslate,
  ifNumber,
  makePlanLongId,
  planDataUtils,
  isMedicareAdvantageType,
  sendTealiumEvent,
  getBenefitCost,
  $$,
  isMapdPlanType,
  isMaPlanType,
} from "../../../helpers";
import { RouteComponentProps, withRouter } from "react-router";
import { StarRating } from "../../../components";
import EnrollButtonForm from "../../../components/EnrollButtonForm";
import { shouldSuppressPlanBenefits } from "../../../helpers/planHelpers";
import routes from "../../../app/routes";

const PremiumSection: FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const t = useTranslate();

  const exclusionOr = (nonExclusionText: string): string => {
    return shouldSuppressPlanBenefits(plan)
      ? t("suppressed_plan_benefit")
      : nonExclusionText;
  };

  let label = "";

  if (isMapdPlanType(plan.plan_type)) {
    label = t("plan_terms.monthly_plan_premium_mapd");
  } else if (isMaPlanType(plan.plan_type)) {
    label = t("plan_card.monthly_premium_ma");
  } else {
    label = t("plan_terms.monthly_plan_premium_pdp");
  }

  return (
    <section className="planCompareCard__premiumSection">
      <p className="ds-h2">
        {exclusionOr($$(plan.calculated_monthly_premium))}
      </p>
      <p className="planCompareCard__premiumSection__label">{label}</p>
    </section>
  );
};

const DeductibleSection: FunctionComponent<{ plan: Plan }> = ({ plan }) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const { mctCurrentPlanYear } = useFlags();

  const exclusionOr = (nonExclusionText: string): string => {
    return shouldSuppressPlanBenefits(plan)
      ? t("suppressed_plan_benefit")
      : nonExclusionText;
  };

  return isMedicareAdvantageType(plan.plan_type) ? (
    <section className="planCompareCard__deductibleSection">
      <div className="planCompareCard__deductibleSection__subSection">
        <p className="ds-h2">{exclusionOr(ifNumber(plan.annual_deductible))}</p>
        <p className="planCompareCard__deductibleSection__label ds-u-margin-bottom--2">
          {t("plan_terms.health_plan_deductible")}
        </p>
      </div>
      {isMapdPlanType(plan.plan_type) && (
        <div className="planCompareCard__deductibleSection__subSection">
          <p className="ds-h2">
            {exclusionOr(
              planDataUtils.getAnnualDrugDeductible(
                state,
                mctCurrentPlanYear,
                plan
              )
            )}
          </p>
          <p className="planCompareCard__deductibleSection__label">
            {t("plan_terms.drug_plan_deductible")}
          </p>
        </div>
      )}
      <div className="planCompareCard__deductibleSection__subSection">
        <p className="ds-h2">
          {exclusionOr(getBenefitCost(t, { plan, benefit: "maximum_oopc" }))}
        </p>
        <p className="planCompareCard__deductibleSection__label">
          {t("plan_terms.oopc_maximum")}
        </p>
      </div>
    </section>
  ) : (
    <section className="planCompareCard__deductibleSection">
      <p className="ds-h2">
        {exclusionOr(
          planDataUtils.getAnnualDrugDeductible(state, mctCurrentPlanYear, plan)
        )}
      </p>
      <p className="planCompareCard__deductibleSection__label">
        {t("pdp.plan_details.yearly_drug_deductible")}
      </p>
    </section>
  );
};

const PlanSummaryCard: FunctionComponent<{
  isMobile?: boolean;
  noDetails?: boolean;
  plan: Plan;
  onPlanRemove: (plan: Plan) => void;
  isCurrentPlan?: boolean;
  isNextYearOfCurrentPlan?: boolean;
} & RouteComponentProps> = ({
  isMobile = false,
  history,
  location,
  noDetails = false,
  plan,
  onPlanRemove,
  isCurrentPlan,
  isNextYearOfCurrentPlan,
}) => {
  const t = useTranslate();
  const goToPlan = (): void =>
    history.push(`${routes.planDetails.replace(":id", makePlanLongId(plan))}`);
  const aria_string = `${t("plan_card.enroll_in")} ${plan.name}`;

  const handlePlanRemove = (): void => {
    location.search = location.search.replace(
      `plans=${makePlanLongId(plan)}`,
      ""
    );
    const matches = location.search.match(/plans/gi);

    onPlanRemove(plan);
    if (matches && matches.length > 1) {
      history.replace(location);
    } else {
      history.goBack();
    }
  };

  return (
    <div
      className={`planCompareCard${
        isCurrentPlan || isNextYearOfCurrentPlan ? " currentPlan" : ""
      }`}
    >
      <Button
        variation="transparent"
        size="small"
        className="ds-c-button--primary-alt planCompareCard__closeButton"
        aria-label={`Remove ${plan.name} plan`}
        onClick={handlePlanRemove}
      >
        <Close />
      </Button>
      <section className="planCompareCard__detailsSection">
        <h4 className="planCompareCard__detailsHeader">{plan.name}</h4>
        {!noDetails && (
          <>
            {!isMobile && (
              <StarRating
                hideTooltip={true}
                highPerforming={plan.high_performing}
                lowPerforming={plan.low_performing}
                stars={plan.overall_star_rating}
                planYear={plan.contract_year}
              />
            )}
            <PremiumSection plan={plan} />
            <DeductibleSection plan={plan} />
          </>
        )}
      </section>
      <section className="planCompareCard__actionSection">
        <Button
          className="ds-c-button--primary-alt ds-u-margin-bottom--1 e2e-compare-plan-details"
          onClick={(): void => {
            // istanbul ignore next: tested elsewhere
            sendTealiumEvent(
              "Find Plans - Compare",
              `Plan Click: ${plan.name}`
            );
            goToPlan();
          }}
          aria-label={"Plan details for " + plan.name}
        >
          {t("plan_card.plan_details")}
        </Button>

        <EnrollButtonForm
          plan={plan}
          aria_string={aria_string}
          tealiumEventAction="Find Plans - Compare"
        />
      </section>
    </div>
  );
};

export default withRouter(PlanSummaryCard);
